import TeacherPageHeader from '../../components/TeacherPageHeader';
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { modificationAPI } from '../../services/modificationAPI';
import { LessonScheduleModification } from '../../types/api';
import Modal from '../../components/Modal';
import Tabs from '../../components/Tabs';
import { STATUS_LABELS, STATUS_COLORS } from '../../config/constants';
import { formatDateLong, getWeekdayLabel } from '../../utils/formatters';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { useIsMobile } from '../../hooks/useIsMobile';
import { PageLoading, EmptyState } from '../../components';

const LessonModifications: React.FC = () => {
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [isUpdating, setIsUpdating] = useState<number | null>(null);

  // Infinite scroll hook
  const {
    items: requests,
    isLoading,
    isLoadingMore,
    error,
    refresh,
    handleScroll
  } = useInfiniteScroll<LessonScheduleModification>({
    fetchFunction: modificationAPI.getModificationRequests,
    limit: 200,
    searchTerm: '',
    statusFilter: selectedStatus,
    additionalParams: {
      requestType: 'change_time,makeup_lesson,reschedule' // 취소 요청 제외
    }
  });

  // 상세보기 모달 관련 상태
  const [teacherResponse, setTeacherResponse] = useState('');
  const [selectedRequest, setSelectedRequest] = useState<LessonScheduleModification | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const isMobile = useIsMobile();

  // 데스크탑: 하단 고정 상세 패널 / 모바일: 모달
  const openDetail = (request: LessonScheduleModification) => {
    setSelectedRequest(request);
    setTeacherResponse('');
    if (isMobile) setShowDetailModal(true);
  };

  // 기본으로 가장 최신 요청의 상세 표시
  useEffect(() => {
    if (isMobile || requests.length === 0) return;
    setSelectedRequest(prev =>
      prev && requests.some(r => r.id === prev.id)
        ? requests.find(r => r.id === prev.id) || requests[0]
        : requests[0]
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requests, isMobile]);

  const statusLabels = STATUS_LABELS.MODIFICATION;
  const statusColors = STATUS_COLORS;

  const handleApprove = async (requestId: number) => {
    if (!selectedRequest) {
      toast.error('요청 정보를 찾을 수 없습니다.');
      return;
    }

    try {
      setIsUpdating(requestId);

      // 변경 내용 정보 구성
      const changeDetails = {
        requestedTime: `${selectedRequest.requestedStartTime?.replace(':00', '')} - ${selectedRequest.requestedEndTime?.replace(':00', '')}`,
        requestedDate: selectedRequest.requestedDate ? formatDateLong(selectedRequest.requestedDate) : null,
        requestedWeekDay: selectedRequest.requestedWeekDay ? getWeekdayLabel(selectedRequest.requestedWeekDay) : null,
        changeType: getChangeTypeLabel(selectedRequest.changeType),
        changePeriod: selectedRequest.changePeriod ? getChangePeriodLabel(selectedRequest.changePeriod) : null,
        changeStartDate: selectedRequest.changeStartDate ? formatDateLong(selectedRequest.changeStartDate) : null,
        changeEndDate: selectedRequest.changeEndDate ? formatDateLong(selectedRequest.changeEndDate) : null
      };

      // 상세한 승인 메시지 구성 (자동 생성)
      let detailedResponse = `승인되었습니다.\n\n[변경 승인 내용]\n`;
      detailedResponse += `• 희망 시간: ${changeDetails.requestedTime}\n`;

      if (changeDetails.requestedDate) {
        detailedResponse += `• 희망 날짜: ${changeDetails.requestedDate}\n`;
      }

      if (changeDetails.requestedWeekDay) {
        detailedResponse += `• 희망 요일: ${changeDetails.requestedWeekDay}\n`;
      }

      detailedResponse += `• 변경 유형: ${changeDetails.changeType}\n`;

      if (selectedRequest.changeType === 'temporary' && changeDetails.changeStartDate && changeDetails.changeEndDate) {
        if (selectedRequest.changePeriod === 'period') {
          detailedResponse += `• 변경 기간: ${changeDetails.changeStartDate} ~ ${changeDetails.changeEndDate}\n`;
        } else if (selectedRequest.changePeriod === 'custom') {
          detailedResponse += `• 변경 날짜: ${changeDetails.changeStartDate} → ${changeDetails.changeEndDate}\n`;
        }
      }

      const response = await modificationAPI.approveModificationRequest(requestId.toString(), detailedResponse);

      if (response.success) {
        toast.success('변경 요청이 승인되었습니다.');
        refresh();

        // 사이드바 카운트 새로고침
        window.dispatchEvent(new Event('refreshPendingCounts'));

        setShowDetailModal(false);
        setSelectedRequest(null);
        setTeacherResponse('');
      } else {
        toast.error(response.message || '승인 처리에 실패했습니다.');
      }
    } catch {
      toast.error('승인 처리 중 오류가 발생했습니다.');
    } finally {
      setIsUpdating(null);
    }
  };

  const handleReject = async (requestId: number) => {
    if (!teacherResponse.trim()) {
      toast.error('거절 사유를 입력해주세요.');
      return;
    }

    if (!selectedRequest) {
      toast.error('요청 정보를 찾을 수 없습니다.');
      return;
    }

    try {
      setIsUpdating(requestId);

      // 변경 내용 정보 구성
      const changeDetails = {
        requestedTime: `${selectedRequest.requestedStartTime?.replace(':00', '')} - ${selectedRequest.requestedEndTime?.replace(':00', '')}`,
        requestedDate: selectedRequest.requestedDate ? formatDateLong(selectedRequest.requestedDate) : null,
        requestedWeekDay: selectedRequest.requestedWeekDay ? getWeekdayLabel(selectedRequest.requestedWeekDay) : null,
        changeType: getChangeTypeLabel(selectedRequest.changeType),
        changePeriod: selectedRequest.changePeriod ? getChangePeriodLabel(selectedRequest.changePeriod) : null,
        changeStartDate: selectedRequest.changeStartDate ? formatDateLong(selectedRequest.changeStartDate) : null,
        changeEndDate: selectedRequest.changeEndDate ? formatDateLong(selectedRequest.changeEndDate) : null
      };

      // 상세한 거절 메시지 구성
      let detailedResponse = `${teacherResponse}\n\n[요청하신 변경 내용]\n`;
      detailedResponse += `• 희망 시간: ${changeDetails.requestedTime}\n`;

      if (changeDetails.requestedDate) {
        detailedResponse += `• 희망 날짜: ${changeDetails.requestedDate}\n`;
      }

      if (changeDetails.requestedWeekDay) {
        detailedResponse += `• 희망 요일: ${changeDetails.requestedWeekDay}\n`;
      }

      detailedResponse += `• 변경 유형: ${changeDetails.changeType}\n`;

      if (selectedRequest.changeType === 'temporary' && changeDetails.changeStartDate && changeDetails.changeEndDate) {
        if (selectedRequest.changePeriod === 'period') {
          detailedResponse += `• 변경 기간: ${changeDetails.changeStartDate} ~ ${changeDetails.changeEndDate}\n`;
        } else if (selectedRequest.changePeriod === 'custom') {
          detailedResponse += `• 변경 날짜: ${changeDetails.changeStartDate} → ${changeDetails.changeEndDate}\n`;
        }
      }

      const response = await modificationAPI.rejectModificationRequest(requestId.toString(), detailedResponse);

      if (response.success) {
        toast.success('변경 요청이 거절되었습니다.');
        refresh();

        // 사이드바 카운트 새로고침
        window.dispatchEvent(new Event('refreshPendingCounts'));

        setShowDetailModal(false);
        setSelectedRequest(null);
        setTeacherResponse('');
      } else {
        toast.error(response.message || '거절 처리에 실패했습니다.');
      }
    } catch {
      toast.error('거절 처리 중 오류가 발생했습니다.');
    } finally {
      setIsUpdating(null);
    }
  };

  const getChangeTypeLabel = (changeType: string) => {
    const typeMap = {
      permanent: '영구 변경',
      temporary: '임시 변경'
    };
    return typeMap[changeType as keyof typeof typeMap] || changeType;
  };

  const getChangePeriodLabel = (changePeriod: string) => {
    const periodMap = {
      custom: '날짜 지정',
      period: '기간 지정'
    };
    return periodMap[changePeriod as keyof typeof periodMap] || changePeriod;
  };

  if (isLoading && requests.length === 0) {
    return <PageLoading message="수업 일정 변경 요청 목록을 불러오는 중..." fullScreen />;
  }

  return (
    <div className="teacher-page list-detail-page">
      <TeacherPageHeader title="일정 변경관리">
        <Tabs
          options={[
            { value: '', label: '전체' },
            { value: 'pending', label: '대기중' },
            { value: 'approved', label: '승인됨' },
            { value: 'rejected', label: '거절됨' }
          ]}
          value={selectedStatus}
          onChange={(value) => setSelectedStatus(value)}
        />
      </TeacherPageHeader>
      {/* Filter (모바일 전용) */}
      <div className="content-header desktop-hidden">
        <Tabs
          options={[
            { value: '', label: '전체' },
            { value: 'pending', label: '대기중' },
            { value: 'approved', label: '승인됨' },
            { value: 'rejected', label: '거절됨' }
          ]}
          value={selectedStatus}
          onChange={(value) => setSelectedStatus(value)}
        />
      </div>

      <div className="card-body" onScroll={handleScroll}>
        {error && (
          <div className="error-message" style={{ padding: '20px', textAlign: 'center', color: '#d32f2f' }}>
            {error}
          </div>
        )}
        {requests.length > 0 ? (
          <>
            {/* 데스크탑 테이블 뷰 */}
            <div className="table-view">
              <div className="table-wrapper">
                <table className="lesson-requests-table">
                  <thead>
                    <tr>
                      <th>학생</th>
                      <th>연락처</th>
                      <th>희망시간</th>
                      <th>신청일</th>
                      <th>액션</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map((request) => (
                      <tr
                        key={request.id}
                        className={`status-row status-${request.status}`}
                        onClick={() => openDetail(request)}
                      >
                        <td>
                          <div className="name-cell">
                            <span className="name">{request.user?.name || 'N/A'}</span>
                            <span className="sub">{request.changeType === 'permanent' ? '영구변경' : '임시변경'}</span>
                          </div>
                        </td>
                        <td>{request.user?.phoneNumber || 'N/A'}</td>
                        <td className="time-cell">
                          {request.requestedStartTime?.replace(':00', '')} - {request.requestedEndTime?.replace(':00', '')}
                          {request.requestedWeekDay && ` (${getWeekdayLabel(request.requestedWeekDay)})`}
                        </td>
                        <td>{formatDateLong(request.createdAt)}</td>
                        <td onClick={(e) => e.stopPropagation()}>
                          {request.status === 'pending' && (
                            <div className="action-buttons">
                              <button
                                className="btn-action approve"
                                onClick={() => openDetail(request)}
                                disabled={isUpdating === request.id}
                              >
                                처리
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 모바일 리스트 뷰 */}
            <div className="card-view">
              <div className="requests-list">
                {requests.map((request) => (
                  <div
                    key={request.id}
                    className={`request-item status-${request.status}`}
                    onClick={() => openDetail(request)}
                  >
                    <div className="item-row">
                      <span className="item-name">{request.user?.name || 'N/A'}</span>
                      <span className="item-date">{formatDateLong(request.createdAt)}</span>
                    </div>
                    <div className="item-row">
                      <span className="item-phone">{request.user?.phoneNumber || 'N/A'}</span>
                    </div>
                    <div className="item-row">
                      <span className="item-time">
                        {request.requestedStartTime?.replace(':00', '')} - {request.requestedEndTime?.replace(':00', '')}
                        {request.requestedWeekDay && ` (${getWeekdayLabel(request.requestedWeekDay)})`}
                      </span>
                    </div>
                    {request.status === 'pending' && (
                      <div className="item-actions" onClick={(e) => e.stopPropagation()}>
                        <button
                          className="btn-approve"
                          onClick={() => openDetail(request)}
                        >
                          처리
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Loading more indicator */}
            {isLoadingMore && (
              <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                추가 데이터 로딩 중...
              </div>
            )}
          </>
        ) : !isLoading ? (
          <EmptyState
            title="수업 일정 변경 요청이 없습니다"
            description="변경 요청이 들어오면 여기에 표시됩니다."
          />
        ) : null}
      </div>

      {/* 데스크탑 하단 고정 상세 */}
      {selectedRequest && !isMobile && (
        <div className="request-detail-inline table-view">
          <div className="detail-main">
            <div className="detail-title-row">
              <div className="detail-title">변경 요청 상세 — {selectedRequest.user?.name || 'N/A'}</div>
              <span className={`status-badge ${statusColors[selectedRequest.status]}`}>
                {statusLabels[selectedRequest.status]}
              </span>
              <span className="badge badge-secondary">
                {selectedRequest.changeType === 'permanent' ? '영구변경' : '임시변경'}
              </span>
              {selectedRequest.status === 'pending' && (
                <div className="title-actions">
                  <button
                    className="mini-btn primary"
                    onClick={() => handleApprove(selectedRequest.id)}
                    disabled={isUpdating === selectedRequest.id}
                  >
                    {isUpdating === selectedRequest.id ? '처리중...' : '승인하기'}
                  </button>
                  <button
                    className="mini-btn danger"
                    onClick={() => handleReject(selectedRequest.id)}
                    disabled={isUpdating === selectedRequest.id}
                  >
                    {isUpdating === selectedRequest.id ? '처리중...' : '거절하기'}
                  </button>
                </div>
              )}
              <button className="detail-close" onClick={() => setSelectedRequest(null)} aria-label="닫기">✕</button>
            </div>
            <div className="detail-grid">
              <div>
                <div className="dt">희망 시간</div>
                <div className="dd">
                  {selectedRequest.requestedStartTime?.replace(':00', '')} - {selectedRequest.requestedEndTime?.replace(':00', '')}
                  {selectedRequest.requestedWeekDay && ` (${getWeekdayLabel(selectedRequest.requestedWeekDay)})`}
                </div>
              </div>
              <div>
                <div className="dt">{selectedRequest.changeType === 'temporary' && selectedRequest.changePeriod === 'period' ? '적용 기간' : '희망 날짜'}</div>
                <div className="dd">
                  {selectedRequest.requestedDate
                    ? formatDateLong(selectedRequest.requestedDate)
                    : selectedRequest.changeStartDate && selectedRequest.changeEndDate
                      ? `${formatDateLong(selectedRequest.changeStartDate)} ~ ${formatDateLong(selectedRequest.changeEndDate)}`
                      : '—'}
                </div>
              </div>
              <div>
                <div className="dt">연락처</div>
                <div className="dd">{selectedRequest.user?.phoneNumber || 'N/A'}</div>
              </div>
              <div>
                <div className="dt">신청일</div>
                <div className="dd">{formatDateLong(selectedRequest.createdAt)}</div>
              </div>
            </div>
            {(selectedRequest.reason || (selectedRequest as any).requestMessage) && (
              <div className="detail-message">{selectedRequest.reason || (selectedRequest as any).requestMessage}</div>
            )}
            {selectedRequest.teacherResponse && (
              <div className="detail-reply">
                <div className="reply-label">처리 결과</div>
                <div className="reply-body">{selectedRequest.teacherResponse}</div>
              </div>
            )}
            {selectedRequest.status === 'pending' && (
              <div className="detail-reply-form">
                <textarea
                  value={teacherResponse}
                  onChange={(e) => setTeacherResponse(e.target.value)}
                  placeholder="거절 사유 (거절 시 필수 입력)"
                  rows={2}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* 상세보기 모달 */}
      <Modal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        title="일정 변경 요청"
        size="sm"
      >
        {selectedRequest && (
          <div className="detail-content compact">
            <div className="detail-header">
              <div className="name-row">
                <span className="student-name">{selectedRequest.user?.name || 'N/A'}</span>
                {selectedRequest.changeType === 'permanent' && (
                  <span className="badge-foreign" style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>영구변경</span>
                )}
                <span className={`status-badge ${statusColors[selectedRequest.status]}`}>
                  {statusLabels[selectedRequest.status]}
                </span>
              </div>
              <div className="meta-info">
                {selectedRequest.user?.phoneNumber || 'N/A'}
              </div>
            </div>

            <div className="detail-info-list">
              <div className="info-row">
                <span className="label">희망시간</span>
                <span className="value">{selectedRequest.requestedStartTime?.replace(':00', '')} - {selectedRequest.requestedEndTime?.replace(':00', '')}</span>
              </div>
              {selectedRequest.requestedWeekDay && (
                <div className="info-row">
                  <span className="label">희망요일</span>
                  <span className="value">{getWeekdayLabel(selectedRequest.requestedWeekDay)}</span>
                </div>
              )}
              {selectedRequest.requestedDate && (
                <div className="info-row">
                  <span className="label">희망날짜</span>
                  <span className="value">{formatDateLong(selectedRequest.requestedDate)}</span>
                </div>
              )}
              {selectedRequest.changeType === 'temporary' && selectedRequest.changeStartDate && selectedRequest.changeEndDate && (
                <div className="info-row">
                  <span className="label">{selectedRequest.changePeriod === 'period' ? '기간' : '날짜'}</span>
                  <span className="value">
                    {formatDateLong(selectedRequest.changeStartDate)} {selectedRequest.changePeriod === 'period' ? '~' : '→'} {formatDateLong(selectedRequest.changeEndDate)}
                  </span>
                </div>
              )}
              {(selectedRequest.reason || (selectedRequest as any).requestMessage) && (
                <div className="info-row">
                  <span className="label">사유</span>
                  <span className="value">{selectedRequest.reason || (selectedRequest as any).requestMessage}</span>
                </div>
              )}
              <div className="info-row">
                <span className="label">신청일</span>
                <span className="value">{formatDateLong(selectedRequest.createdAt)}</span>
              </div>
            </div>

            {selectedRequest.status === 'pending' && (
              <div className="form-group" style={{ marginTop: '16px' }}>
                <label style={{ fontSize: '12px', color: '#6b7280', marginBottom: '6px', display: 'block' }}>거절 사유 (거절 시에만)</label>
                <textarea
                  className="form-textarea"
                  value={teacherResponse}
                  onChange={(e) => setTeacherResponse(e.target.value)}
                  placeholder="거절 사유를 입력해주세요"
                  rows={3}
                  style={{ minHeight: '80px' }}
                />
              </div>
            )}

            {selectedRequest.teacherResponse && (
              <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>처리 결과</div>
                <div style={{ fontSize: '14px', color: '#374151', whiteSpace: 'pre-wrap' }}>{selectedRequest.teacherResponse}</div>
              </div>
            )}

            {selectedRequest.status === 'pending' && (
              <div className="modal-actions">
                <button
                  className="btn btn-primary"
                  onClick={() => handleApprove(selectedRequest.id)}
                  disabled={isUpdating === selectedRequest.id}
                >
                  {isUpdating === selectedRequest.id ? '처리중...' : '승인'}
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => handleReject(selectedRequest.id)}
                  disabled={isUpdating === selectedRequest.id}
                >
                  {isUpdating === selectedRequest.id ? '처리중...' : '거절'}
                </button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default LessonModifications; 